import React from 'react';
import { EmptyNote } from '../ui/EmptyNote';
import { Sheet } from '../ui/Sheet';
import { Button } from '../../ui/Button/Button';
import { LOAD_ICON_MAP } from '../../../lib/rcu/catalog/icon';
import { LOAD_TYPE_SPECS } from '../../../lib/rcu/catalog/loadType';
import type { Room } from '../../../lib/rcu/model';
import { findRoom } from '../../../lib/rcu/query/lookup';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useCommandFeedback } from '../useCommandFeedback';

export interface RoomLoadsSheetProps {
  isOpen: boolean;
  /** The room receiving loads, or null when the sheet should stay closed. */
  room: Room | null;
  onClose: () => void;
}

/**
 * Adds loads to a room. Tapping assigns immediately — moving a load from
 * another room is the same single tap, since a load belongs to one room only.
 */
export const RoomLoadsSheet: React.FC<RoomLoadsSheetProps> = ({ isOpen, room, onClose }) => {
  const config = useRcuConfigStore((state) => state.config);
  const moveLoadToRoom = useRcuConfigStore((state) => state.moveLoadToRoom);
  const report = useCommandFeedback();

  if (room === null) return null;

  const candidates = config.loads.filter((load) => load.roomId !== room.id);

  return (
    <Sheet
      isOpen={isOpen}
      title={`Add loads to ${room.name}`}
      subtitle={`${config.loads.filter((load) => load.roomId === room.id).length} load(s) already here`}
      onClose={onClose}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      }
    >
      {candidates.length === 0 ? (
        <EmptyNote message="Every load is already in this room." />
      ) : (
        <ul className="space-y-2">
          {candidates.map((load) => {
            const Icon = LOAD_ICON_MAP[load.iconKey];
            const currentRoom = load.roomId === null ? null : findRoom(config, load.roomId)?.name ?? null;
            return (
              <li key={load.id}>
                <button
                  type="button"
                  onClick={() => {
                    report(moveLoadToRoom(load.id, room.id), `${load.name} added to ${room.name}.`);
                  }}
                  className="flex w-full items-center gap-3 rounded-2xl border border-[#E0E0E0] bg-white px-4 py-3 text-left transition-colors hover:border-[#D4A054]"
                >
                  <Icon className="h-5 w-5 shrink-0 text-[#D4A054]" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-[#333333]">
                      {load.name}
                    </span>
                    <span className="block text-xs text-[#666666]">
                      {LOAD_TYPE_SPECS[load.type].label} · {currentRoom ?? 'Unassigned'}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Sheet>
  );
};

export default RoomLoadsSheet;
